import classNames from 'classnames';
import type { Chord as D3Chord } from 'd3';

import styles from './chord-chart.module.css';
import { ChordDirectionTriangle } from './chord-direction-indicator';
import { ChordRibbonTooltip } from './chord-ribbon-tooltip';
import {
  getRadialTooltipAnchor,
  getRadialTooltipPlacement,
} from './chord-tooltip-position';
import type { ChordFlowMode, ChordShareDirection } from './types';

const GRADIENT_MID_OFFSET = '50%';
const GRADIENT_SOURCE_REST = 0.12;
const GRADIENT_MID_REST = 0.22;
const GRADIENT_TARGET_REST = 0.95;
const GRADIENT_SOURCE_HOVER = 0.2;
const GRADIENT_MID_HOVER = 0.42;
const GRADIENT_TARGET_HOVER = 1;

type ChordRibbonProps = {
  chord: D3Chord;
  getRibbonPath: (chord: D3Chord) => string | null;
  innerRadius: number;
  outerRadius: number;
  labelOffset: number;
  labelFontSize: number;
  color: string;
  gradientId: string;
  sourceLabel: string;
  targetLabel: string;
  flow: number;
  share: number | undefined;
  shareDirection: ChordShareDirection;
  flowMode: ChordFlowMode;
  isFaded: boolean;
  isHovered: boolean;
  isHighlighted: boolean;
  onHoverStart: () => void;
  onHoverEnd: () => void;
};

export const ChordRibbon = ({
  chord,
  getRibbonPath,
  innerRadius,
  outerRadius,
  labelOffset,
  labelFontSize,
  color,
  gradientId,
  sourceLabel,
  targetLabel,
  flow,
  share,
  shareDirection,
  flowMode,
  isFaded,
  isHovered,
  isHighlighted,
  onHoverStart,
  onHoverEnd,
}: ChordRibbonProps) => {
  const ribbonPath = getRibbonPath(chord);

  const sourceMidAngle = (chord.source.startAngle + chord.source.endAngle) / 2;
  const targetMidAngle = (chord.target.startAngle + chord.target.endAngle) / 2;
  const sourceMid = polar(sourceMidAngle, innerRadius);
  const targetMid = polar(targetMidAngle, innerRadius);

  const targetArcLength =
    (chord.target.endAngle - chord.target.startAngle) * innerRadius;

  // Anchor the tooltip outside the ring at the target side: that's where the
  // direction triangle sits and where the gradient is most opaque, so the
  // tooltip reads as the "destination" of the flow.
  const tooltipAnchor = getRadialTooltipAnchor({
    angle: targetMidAngle,
    outerRadius,
    labelOffset,
    labelFontSize,
  });
  const tooltipPlacement = getRadialTooltipPlacement(targetMidAngle);

  const sourceOpacity = isHighlighted
    ? GRADIENT_SOURCE_HOVER
    : GRADIENT_SOURCE_REST;
  const midOpacity = isHighlighted ? GRADIENT_MID_HOVER : GRADIENT_MID_REST;
  const targetOpacity = isHighlighted
    ? GRADIENT_TARGET_HOVER
    : GRADIENT_TARGET_REST;

  return (
    <g
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      className={classNames(styles.group, { [styles.faded ?? '']: isFaded })}
    >
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={sourceMid.x}
          y1={sourceMid.y}
          x2={targetMid.x}
          y2={targetMid.y}
        >
          <stop
            className={styles.gradientStop}
            offset="0%"
            stopColor={color}
            stopOpacity={sourceOpacity}
          />
          <stop
            className={styles.gradientStop}
            offset={GRADIENT_MID_OFFSET}
            stopColor={color}
            stopOpacity={midOpacity}
          />
          <stop
            className={styles.gradientStop}
            offset="100%"
            stopColor={color}
            stopOpacity={targetOpacity}
          />
        </linearGradient>
      </defs>
      <path
        d={ribbonPath ?? undefined}
        fill={`url(#${gradientId})`}
        className={styles.ribbon}
        data-testid="chord-chart__ribbon"
      />
      <ChordDirectionTriangle
        targetMidAngle={targetMidAngle}
        targetArcLength={targetArcLength}
        innerRadius={innerRadius}
        color={color}
        isHighlighted={isHighlighted}
      />
      <ChordRibbonTooltip
        sourceLabel={sourceLabel}
        targetLabel={targetLabel}
        flow={flow}
        share={share}
        shareDirection={shareDirection}
        flowMode={flowMode}
        isOpen={isHovered}
        placement={tooltipPlacement}
      >
        <circle
          r={0}
          cx={tooltipAnchor.x}
          cy={tooltipAnchor.y}
          pointerEvents="none"
        />
      </ChordRibbonTooltip>
    </g>
  );
};

const polar = (angle: number, radius: number): { x: number; y: number } => ({
  x: radius * Math.sin(angle),
  y: -radius * Math.cos(angle),
});
